import { constants as performanceConstants } from '@saucelabs/sl-module-performance';
import { constants as jobConstants } from '@saucelabs/sl-module-jobs';

import { PERFORMANCE_TRACE_COMMAND_DELAY_IN_MS, SAUCE_JANKINESS_CUSTOM_COMMAND } from './constants';

export function sanitizeCommandId({ hash }) {
  if (hash) {
    const hashValue = (hash[0] === '#') ? hash.substr(1) : hash;
    const commandId = parseInt(hashValue, 10);

    if (Number.isFinite(commandId)) {
      return commandId;
    }
  }
  return null;
}

const commandMatch = (phrase, command) => {
  const searchableFields = [
    command.method,
    command.getMethod(),
    command.path,
    JSON.stringify(command.request),
    JSON.stringify(command.result),
  ];
  return phrase.test(searchableFields.join(' '));
};

// performance log contains url with forward slash at the end i.e. https://saucelabs.com/
// And command log contains url without last slash i.e. https://saucelabs.com,
// to remove this difference url.replace is needed
export const findPerformanceDataByURL = ({ performanceData, addedLoaderIds, commandURL }) => (
  performanceData.find(({ url, loaderId }) => (
    url && !addedLoaderIds.includes(loaderId)) &&
    url.replace(/\/$/, '') === commandURL.replace(/\/$/, ''),
  )
);

// If performance's trace timestamp is greater than command's start_time
// and if difference between them is less than PERFORMANCE_TRACE_COMMAND_DELAY_IN_MS time.
export const findPerformanceDataByTimestamp = ({ command, performanceData, addedLoaderIds }) => (
  performanceData.find(({ timestamp, loaderId }) => (
    !addedLoaderIds.includes(loaderId) &&
    command.get('start_time') * 1000 <= timestamp &&
    timestamp - command.get('start_time') * 1000 <= PERFORMANCE_TRACE_COMMAND_DELAY_IN_MS
  ))
);
export const findRegressionData = (regressions, filterBy) => regressions.find(
  ({ url, order_index: orderIndex }) => url === filterBy.url && orderIndex === filterBy.orderIndex,
);

// Add performance trace data of URL in related command log (either in LOAD URL or Click command)
export const includePerformanceInCommands = ({
  commands, performanceData, performanceRegressions,
}) => {
  // Trace added loaderIds to remove redundancy
  const addedLoaderIds = [];

  const updateCommand = (command, type) => {
    let filteredPerformanceData;
    const method = command.get('method');
    const path = command.get('path');
    const request = command.get('request');

    if (type === 'url' && path === 'url' && method === 'POST') {
      filteredPerformanceData = findPerformanceDataByURL({
        performanceData,
        addedLoaderIds,
        commandURL: request ? request.url : command.get('result'),
      });
    }

    if ((type === 'click' && path.includes('/click')) || (path.includes('execute/sync') && request.script === SAUCE_JANKINESS_CUSTOM_COMMAND)) {
      filteredPerformanceData = findPerformanceDataByTimestamp({
        command, performanceData, addedLoaderIds,
      });
    }

    if (filteredPerformanceData) {
      addedLoaderIds.push(filteredPerformanceData.loaderId);
      return command.withMutations((newCommand) => {
        newCommand
          .set('performanceData', filteredPerformanceData)
          .set('regressionData', findRegressionData(performanceRegressions, filteredPerformanceData));
      });
    }
    return command;
  };

  // First check for URL commands
  const newCommands = commands.map((command) => updateCommand(command, 'url'));

  // If all performance data is added in command logs then return it
  if (addedLoaderIds.length === performanceData.size) {
    return newCommands;
  }

  // Next check for Click commands in reverse list of logs
  // to find the exact click command according to timestamp
  return newCommands.reverse().map((command) => updateCommand(command, 'click')).reverse();
};

export const filterCommands = ({
  commands, filterState, performanceData, performanceRegressions,
}) => {
  const { phrase, screenshotOnly } = filterState;
  const newCommands = performanceData && performanceData.size ?
    includePerformanceInCommands({ commands, performanceData, performanceRegressions }) : commands;
  if (!(phrase || screenshotOnly)) {
    return newCommands;
  }
  const regexPhrase = new RegExp(phrase, 'i');
  return newCommands.filter((command) => {
    if (screenshotOnly && !command.hasScreenshot()) {
      return false;
    }
    if (phrase && !commandMatch(regexPhrase, command)) {
      return false;
    }
    return true;
  });
};

export function newActiveCommand(commands, offset, currentActive) {
  let newId = currentActive;
  let newCommand;

  // We need to skip certain types of commands so this is a loop:
  do {
    newId += offset;
    if (newId < 0) {
      newId = commands.size - 1;
    } else {
      newId %= commands.size;
    }
    newCommand = commands.get(newId);
  }
  while (newCommand.message);

  return newCommand.get('id');
}

export function preselectedCommandId(commands, hash) {
  const hashCommandId = sanitizeCommandId({ hash });
  if (Number.isFinite(hashCommandId)) {
    return hashCommandId;
  }

  const lastFailedCommandId = commands.findIndex((command) => command.isFailed());

  if (lastFailedCommandId >= 0) {
    return lastFailedCommandId;
  }

  return null;
}

export function screenshotCommandId(commands) {
  let activeCommandIndex = commands.findIndex((command) => command.get('active'));
  if (activeCommandIndex < 0) {
    return null;
  }

  while (
    !commands.get(activeCommandIndex).hasScreenshot() &&
    activeCommandIndex >= 1
  ) {
    activeCommandIndex -= 1;
  }

  if (commands.get(activeCommandIndex).hasScreenshot()) {
    return commands.get(activeCommandIndex);
  }

  return null;
}

export function offsetCommandId(commands, offset, currentActive) {
  let newIndex = currentActive + offset;
  if (newIndex < 0) {
    newIndex = commands.size - 1;
  } else if (newIndex >= commands.size) {
    newIndex = 0;
  }

  if (!commands.get(newIndex)) {
    return null;
  }

  return commands.get(newIndex).get('id');
}

export function isPerformanceAdvancedDisabled({ performanceAccessType }) {
  return performanceAccessType !== performanceConstants.ACCESS_TYPE.ADVANCED;
}

export const createBreadcrumbForLastLocation = (lastLocation) => {
  const defaultLocation = {
    name: 'Dashboard',
    url: '/dashboard/tests',
  };

  if (!lastLocation || !lastLocation.pathname) {
    return defaultLocation;
  }

  const { pathname, search } = lastLocation;
  const url = `${pathname}${search}`;
  if (pathname.startsWith('/performance')) {
    return { name: 'Performance', url };
  }

  if (pathname.startsWith('/archives')) {
    return { name: 'Archives', url };
  }

  if (pathname.startsWith('/builds')) {
    return { name: 'Builds', url };
  }

  if (pathname.startsWith('/dashboard')) {
    return { name: 'Dashboard', url };
  }

  return defaultLocation;
};

export const hasUserAccessToTheJob = ({ user, job }) => {
  if (
    !user.isAdmin() &&
    !user.isOrgAdmin() &&
    job.getVisibility() === jobConstants.VISIBILITY_PRIVATE &&
    job.get('owner') !== user.get('username')
  ) {
    return false;
  }
  return true;
};
